import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronDown, ChevronRight } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog.jsx';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction,
} from '@/components/ui/alert-dialog.jsx';
import { Button } from '@/components/ui/button.jsx';
import { Input } from '@/components/ui/input.jsx';
import { toast } from '@/components/ui/toaster.jsx';
import { mintBatch } from '@/services/tap.service.js';
import BrandImageField from './BrandImageField.jsx';

const MAX_COUNT = 500;

// Mint a batch of TAP tokens. shadcn Dialog form → AlertDialog confirm
// (no window.confirm) → POST /api/tap/admin/batch → route to the new batch.
// Defaults carry the UTM set, the landing copy, and the partner logo (a data
// URI resized in the browser by BrandImageField; the server caps it at 200KB).
export default function MintBatchDialog({ open, onOpenChange, onMinted }) {
  const navigate = useNavigate();
  const [label, setLabel] = useState('');
  const [count, setCount] = useState(5);
  const [showDefaults, setShowDefaults] = useState(false);
  const [d, setD] = useState({ utm_source: '', utm_medium: '', utm_campaign: '', utm_content: '', branding_caption: '', badge_text: '', subhead_text: '', accent_color: '' });
  const [brandImage, setBrandImage] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [minting, setMinting] = useState(false);

  const labelOk = label.trim().length > 0;
  const countOk = Number.isInteger(count) && count >= 1 && count <= MAX_COUNT;
  const canSubmit = labelOk && countOk;

  function reset() {
    setLabel(''); setCount(5); setShowDefaults(false); setBrandImage('');
    setD({ utm_source: '', utm_medium: '', utm_campaign: '', utm_content: '', branding_caption: '', badge_text: '', subhead_text: '', accent_color: '' });
  }

  async function doMint() {
    setMinting(true);
    try {
      const defaults = {};
      for (const [k, v] of Object.entries(d)) if (v.trim()) defaults[k] = v.trim();
      if (brandImage) defaults.branding_image_url = brandImage;
      const res = await mintBatch({
        batch_label: label.trim(),
        count,
        defaults: Object.keys(defaults).length ? defaults : undefined,
      });
      toast.success(`Minted ${res.count} token${res.count === 1 ? '' : 's'} in ${res.batch_label}`);
      setConfirmOpen(false);
      onOpenChange(false);
      onMinted?.();
      reset();
      navigate(`/admin/tap/batch/${encodeURIComponent(res.batch_label)}`);
    } catch (err) {
      toast.error(`Mint failed: ${err.body?.error || err.code || err.message}`);
    } finally {
      setMinting(false);
    }
  }

  const setField = (k) => (e) => setD((prev) => ({ ...prev, [k]: e.target.value }));

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Mint a batch</DialogTitle>
            <DialogDescription>
              Each token becomes a keychain URL. Program the chips after minting.
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4">
            <div>
              <label className="mb-1.5 block text-xs font-medium text-text-muted" htmlFor="mb-label">
                Batch label
              </label>
              <Input
                id="mb-label"
                autoFocus
                placeholder="SNF-2026, OSH-2026, FBO-CAK"
                value={label}
                onChange={(e) => setLabel(e.target.value)}
              />
            </div>

            <div>
              <label className="mb-1.5 block text-xs font-medium text-text-muted" htmlFor="mb-count">
                Count <span className="text-text-dim">(1–{MAX_COUNT})</span>
              </label>
              <Input
                id="mb-count"
                type="number"
                min={1}
                max={MAX_COUNT}
                value={count}
                onChange={(e) => setCount(parseInt(e.target.value, 10) || 0)}
              />
              {!countOk && count !== 0 && (
                <p className="mt-1 text-xs text-accent">Count must be 1–{MAX_COUNT}.</p>
              )}
            </div>

            <button
              type="button"
              onClick={() => setShowDefaults((v) => !v)}
              className="flex items-center gap-1.5 text-sm text-text-muted hover:text-text"
            >
              {showDefaults ? <ChevronDown size={15} /> : <ChevronRight size={15} />}
              Defaults (UTM, logo, copy)
            </button>

            {showDefaults && (
              <div className="grid gap-3 rounded-card border border-border bg-surface-strong/40 p-3">
                {['utm_source', 'utm_medium', 'utm_campaign', 'utm_content'].map((k) => (
                  <div key={k}>
                    <label className="mb-1 block text-[11px] uppercase tracking-wide text-text-dim" htmlFor={`mb-${k}`}>
                      {k.replace('utm_', 'utm · ')}
                    </label>
                    <Input id={`mb-${k}`} value={d[k]} onChange={setField(k)} />
                  </div>
                ))}
                <BrandImageField id="mb-brand-image" value={brandImage} onChange={setBrandImage} />
                <div>
                  <label className="mb-1 block text-[11px] uppercase tracking-wide text-text-dim" htmlFor="mb-caption">
                    Logo caption
                  </label>
                  <Input id="mb-caption" value={d.branding_caption} onChange={setField('branding_caption')} />
                </div>
                <div>
                  <label className="mb-1 block text-[11px] uppercase tracking-wide text-text-dim" htmlFor="mb-badge_text">
                    Badge text
                  </label>
                  <Input id="mb-badge_text" maxLength={40} placeholder="Vouched for" value={d.badge_text} onChange={setField('badge_text')} />
                </div>
                <div>
                  <label className="mb-1 block text-[11px] uppercase tracking-wide text-text-dim" htmlFor="mb-subhead_text">
                    Hero subhead
                  </label>
                  <Input id="mb-subhead_text" maxLength={160} placeholder="A pilot put their name on us. Let's get you covered." value={d.subhead_text} onChange={setField('subhead_text')} />
                </div>
                <div>
                  <label className="mb-1 block text-[11px] uppercase tracking-wide text-text-dim" htmlFor="mb-accent_color">
                    Accent color <span className="text-text-dim/70 normal-case">(hex)</span>
                  </label>
                  <Input id="mb-accent_color" placeholder="#0A1D3C" value={d.accent_color} onChange={setField('accent_color')} />
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button disabled={!canSubmit} onClick={() => setConfirmOpen(true)}>Mint…</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Mint {count} token{count === 1 ? '' : 's'}?</AlertDialogTitle>
            <AlertDialogDescription>
              Creates {count} keychain token{count === 1 ? '' : 's'} in batch “{label.trim()}”. You can revoke any of them later.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={minting}>Back</AlertDialogCancel>
            <AlertDialogAction onClick={doMint} disabled={minting}>
              {minting ? 'Minting…' : `Mint ${count}`}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
